import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend,  } from 'recharts';
import AppLayout from 'layouts/AppLayout';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import ReviewStatusBadge from './components/ReviewStatusBadge';
import { ASSETS, AUDIT_LOG } from './data/assetData';

const TABS = [
  { id: 'passport', label: 'Asset Passport', icon: 'IdCard' },
  { id: 'performance', label: 'Performance', icon: 'TrendingUp' },
  { id: 'underwrite', label: 'Underwrite vs Actual', icon: 'BarChart3' },
  { id: 'debt', label: 'Debt & Structure', icon: 'Layers' },
  { id: 'ic-memo', label: 'IC Memo', icon: 'FileText' },
  { id: 'value-creation', label: 'Value Creation', icon: 'Sparkles' },
  { id: 'risk', label: 'Risk & Counterparty', icon: 'ShieldAlert' },
  { id: 'governance', label: 'Governance', icon: 'BookOpen' },
  { id: 'audit', label: 'Audit Trail', icon: 'ClipboardList' },
];

const WATCHLIST_COLOURS = {
  'Confirmed': 'bg-green-100 text-green-800',
  'Confirmed with actions': 'bg-blue-100 text-blue-800',
  'Watchlist': 'bg-amber-100 text-amber-800',
  'Escalated': 'bg-red-100 text-red-800',
  'Exit case': 'bg-purple-100 text-purple-800',
};

const RISK_COLOURS = [
  '', 'text-green-600', 'text-green-500', 'text-amber-500', 'text-orange-500', 'text-red-600'
];
const RISK_BG = [
  '', 'bg-green-100', 'bg-green-100', 'bg-amber-100', 'bg-orange-100', 'bg-red-100'
];

const VALUE_STATUS_COLOURS = {
  'Delivered': 'bg-green-100 text-green-800',
  'In progress': 'bg-blue-100 text-blue-800',
  'Planned': 'bg-slate-100 text-slate-700',
  'Market': 'bg-purple-100 text-purple-800',
  'Not viable': 'bg-red-100 text-red-700',
};

function InfoRow({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between py-2.5 border-b border-border last:border-0 gap-4">
      <span className="font-caption text-sm text-muted-foreground flex-shrink-0">{label}</span>
      <span className={`text-sm font-medium text-foreground text-right ${mono ? 'font-data' : 'font-caption'}`}>{value}</span>
    </div>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="mb-4">
      <h3 className="font-heading font-semibold text-foreground" style={{ fontSize: '1rem' }}>{title}</h3>
      {subtitle && <p className="font-caption text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  );
}

function Card({ children, className = '' }) {
  return (
    <div className={`bg-card border border-border rounded-lg shadow-xs p-5 ${className}`}>
      {children}
    </div>
  );
}

// ── TAB: ASSET PASSPORT ──────────────────────────────────────────────────────
function PassportTab({ asset, navigate }) {
  return (
    <div className="space-y-6">
      {/* Identity block */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <SectionHeader title="Asset Identity" subtitle="Canonical record — source of truth for IC" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <div>
              <InfoRow label="Legal Entity" value={asset?.name} />
              <InfoRow label="Stage" value={asset?.stage} />
              <InfoRow label="Sector" value={`${asset?.sector} — ${asset?.subSector}`} />
              <InfoRow label="Country" value={asset?.country} />
              <InfoRow label="Fund" value={asset?.fund} />
              <InfoRow label="Fund Exposure" value={`£${asset?.fundExposure}m`} mono />
            </div>
            <div>
              <InfoRow label="Shareholding %" value={`${asset?.shareholdingPct}%`} mono />
              <InfoRow label="Total Investment" value={`£${asset?.totalInvestmentValue}m`} mono />
              <InfoRow label="Acquisition Date" value={asset?.acquisitionDate} mono />
              <InfoRow label="Last Review" value={asset?.lastReviewDate} mono />
              <InfoRow label="Next Review" value={asset?.nextReviewDate} mono />
              <InfoRow label="Debt Stack" value={asset?.debtStack ? 'Yes' : 'No'} />
            </div>
          </div>
          <div className="mt-4 p-4 bg-muted/30 rounded-md">
            <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Description</p>
            <p className="font-body text-sm text-foreground leading-relaxed">{asset?.description}</p>
          </div>
        </Card>

        <div className="space-y-4">
          {/* Status card */}
          <Card>
            <SectionHeader title="Current Status" />
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-caption text-sm text-muted-foreground">Review Status</span>
                <ReviewStatusBadge status={asset?.reviewStatus} />
              </div>
              <div className="flex items-center justify-between">
                <span className="font-caption text-sm text-muted-foreground">Watchlist</span>
                <span className={`text-xs px-2.5 py-0.5 rounded-full font-caption font-semibold ${WATCHLIST_COLOURS?.[asset?.watchlistStatus] || 'bg-gray-100 text-gray-700'}`}>
                  {asset?.watchlistStatus}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-caption text-sm text-muted-foreground">Risk Score</span>
                <span className={`text-sm font-data font-bold ${RISK_COLOURS?.[asset?.riskScore]}`}>{asset?.riskScore}/5</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-caption text-sm text-muted-foreground">Disputes</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-caption font-medium ${
                  asset?.disputesStatus === 'None' ? 'bg-green-100 text-green-800' :
                  asset?.disputesStatus === 'Minor' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                }`}>{asset?.disputesStatus}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="font-caption text-sm text-muted-foreground">Stakeholders</span>
                <span className="font-caption text-sm text-foreground font-medium">{asset?.stakeholderStatus}</span>
              </div>
            </div>
          </Card>

          {/* Quick nav */}
          <Card>
            <SectionHeader title="Quick Navigation" />
            <div className="space-y-1.5">
              {[
                { label: 'Review Pack', path: '/asset-management/review-packs', icon: 'FileText' },
                { label: 'Watchlist & Actions', path: '/asset-management/watchlist-actions', icon: 'AlertCircle' },
                { label: 'Risk Monitor', path: '/asset-management/risk-monitor', icon: 'ShieldAlert' },
                { label: 'Governance Library', path: '/asset-management/governance-library', icon: 'BookOpen' },
                { label: 'Audit Trail', path: '/asset-management/audit-trail-exports', icon: 'ClipboardList' },
              ]?.map((link) => (
                <button
                  key={link?.path}
                  onClick={() => navigate(link?.path)}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-caption text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors text-left"
                >
                  <Icon name={link?.icon} size={13} color="currentColor" />
                  {link?.label}
                  <Icon name="ChevronRight" size={12} color="currentColor" className="ml-auto" />
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Key dates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <SectionHeader title="Key Expiry Dates" />
          {asset?.keyExpiryDates?.map((d, i) => (
            <InfoRow key={i} label={d?.label} value={d?.date} mono />
          ))}
        </Card>
        <Card>
          <SectionHeader title="Key Operational Dates" />
          {asset?.keyOperationalDates?.map((d, i) => (
            <InfoRow key={i} label={d?.label} value={d?.date} mono />
          ))}
        </Card>
      </div>

      {/* Situation update */}
      <Card>
        <SectionHeader title="Current Situation" subtitle="Latest asset manager update" />
        <p className="font-body text-sm text-foreground leading-relaxed">{asset?.situationUpdate}</p>
        {asset?.agreedActions && (
          <div className="mt-3 p-3 bg-primary/5 border border-primary/20 rounded-md">
            <p className="font-caption text-xs font-semibold text-primary mb-1">Agreed Actions</p>
            <p className="font-body text-sm text-foreground">{asset?.agreedActions}</p>
          </div>
        )}
      </Card>
    </div>
  );
}

// ── TAB: PERFORMANCE ─────────────────────────────────────────────────────────
function PerformanceTab({ asset }) {
  return (
    <div className="space-y-6">
      {/* Yield metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-4">
        {[
          { label: 'Investment IRR', value: `${asset?.irr}%`, highlight: true },
          { label: 'Yield Yrs 1–10', value: `${asset?.yield1to10}%` },
          { label: 'Yield Yrs 11–20', value: `${asset?.yield11to20}%` },
          { label: 'Yield Yrs 21–25', value: `${asset?.yield21to25}%` },
          { label: 'Lifetime Yield', value: `${asset?.yieldLifetime}%` },
          { label: 'Multiple', value: `${asset?.multiple}x` },
          { label: 'Cash Yield (LTM)', value: `${asset?.cashYieldActual}%` },
        ]?.map((m) => (
          <div key={m?.label} className={`bg-card border rounded-lg p-4 shadow-xs ${
            m?.highlight ? 'border-primary/40 bg-primary/5' : 'border-border'
          }`}>
            <p className={`font-data text-2xl font-bold ${
              m?.highlight ? 'text-primary' : 'text-foreground'
            }`}>{m?.value}</p>
            <p className="font-caption text-xs text-muted-foreground mt-1">{m?.label}</p>
          </div>
        ))}
      </div>

      {/* IRR evolution chart */}
      <Card>
        <SectionHeader title="IRR Evolution" subtitle="Quarterly IRR progression since acquisition" />
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={asset?.irrHistory} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
            <YAxis domain={['auto', 'auto']} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
            <Tooltip formatter={(v) => [`${v}%`, 'IRR']} contentStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="irr" stroke="var(--color-primary)" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Narratives */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <SectionHeader title="Outperformance Drivers" />
          <p className="font-body text-sm text-foreground leading-relaxed">{asset?.outperformanceDrivers || '—'}</p>
        </Card>
        <Card>
          <SectionHeader title="Underperformance Drivers" />
          <p className="font-body text-sm text-foreground leading-relaxed">{asset?.underperformanceDrivers || '—'}</p>
        </Card>
      </div>

      <Card>
        <SectionHeader title="Asset Timeline" subtitle="Key events since acquisition" />
        <p className="font-body text-sm text-foreground leading-relaxed">{asset?.assetTimeline}</p>
      </Card>
    </div>
  );
}

// ── TAB: UNDERWRITE VS ACTUAL ─────────────────────────────────────────────────
function UnderwriteTab({ asset }) {
  const rows = [
    { metric: 'Investment IRR', underwrite: '10.5%', actual: `${asset?.irr}%`, delta: (asset?.irr - 10.5)?.toFixed(1), positive: asset?.irr >= 10.5 },
    { metric: 'Cash Yield (LTM)', underwrite: `${asset?.cashYieldBudget}%`, actual: `${asset?.cashYieldActual}%`, delta: (asset?.cashYieldActual - asset?.cashYieldBudget)?.toFixed(1), positive: asset?.cashYieldActual >= asset?.cashYieldBudget },
    { metric: 'Equity Multiple', underwrite: '2.0x', actual: `${asset?.multiple}x`, delta: (asset?.multiple - 2.0)?.toFixed(2), positive: asset?.multiple >= 2.0 },
    { metric: 'Yield Yrs 1–10', underwrite: '6.0%', actual: `${asset?.yield1to10}%`, delta: (asset?.yield1to10 - 6.0)?.toFixed(1), positive: asset?.yield1to10 >= 6.0 },
    { metric: 'Yield Yrs 11–20', underwrite: '6.5%', actual: `${asset?.yield11to20}%`, delta: (asset?.yield11to20 - 6.5)?.toFixed(1), positive: asset?.yield11to20 >= 6.5 },
    { metric: 'Lifetime Yield', underwrite: '6.2%', actual: `${asset?.yieldLifetime}%`, delta: (asset?.yieldLifetime - 6.2)?.toFixed(1), positive: asset?.yieldLifetime >= 6.2 },
  ];

  const chartData = rows?.map((r) => ({
    metric: r?.metric?.replace('Investment ', '')?.replace(' (LTM)', ''),
    Underwrite: parseFloat(r?.underwrite),
    Actual: parseFloat(r?.actual),
  }));

  return (
    <div className="space-y-6">
      <Card>
        <SectionHeader title="Underwrite vs Actual" subtitle="Base case assumptions at acquisition vs current actuals" />
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                {['Metric', 'Underwrite (Base Case)', 'Actual / Latest Forecast', 'Variance', 'Signal']?.map((h) => (
                  <th key={h} className="px-4 py-3 text-left font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows?.map((r) => (
                <tr key={r?.metric} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3 font-caption text-sm font-medium text-foreground">{r?.metric}</td>
                  <td className="px-4 py-3 font-data text-sm text-muted-foreground">{r?.underwrite}</td>
                  <td className="px-4 py-3 font-data text-sm font-semibold text-foreground">{r?.actual}</td>
                  <td className="px-4 py-3">
                    <span className={`font-data text-sm font-semibold ${
                      r?.positive ? 'text-green-600' : 'text-red-600'
                    }`}>{r?.positive ? '+' : ''}{r?.delta}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Icon
                      name={r?.positive ? 'TrendingUp' : 'TrendingDown'}
                      size={16}
                      color={r?.positive ? 'var(--color-success)' : 'var(--color-error)'}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <SectionHeader title="Visual Comparison" subtitle="Underwrite vs actual across key metrics" />
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData} barCategoryGap="30%">
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="metric" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip contentStyle={{ fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Underwrite" fill="var(--color-muted-foreground)" radius={[3, 3, 0, 0]} />
            <Bar dataKey="Actual" fill="var(--color-primary)" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card>
        <SectionHeader title="Operational Facts Validated" />
        <p className="font-body text-sm text-foreground leading-relaxed">{asset?.operationalFacts}</p>
      </Card>
    </div>
  );
}

// ── TAB: DEBT & STRUCTURE ─────────────────────────────────────────────────────
function DebtTab({ asset }) {
  const debtLayers = asset?.debtStack ? [
    { tranche: 'Senior Debt', amount: Math.round(asset?.totalInvestmentValue * 0.55), rate: '4.2%', maturity: asset?.keyExpiryDates?.[1]?.date || '2035-12-31', covenant: 'DSCR ≥ 1.20x', headroom: '0.38x', status: 'Compliant' },
    { tranche: 'Mezzanine', amount: Math.round(asset?.totalInvestmentValue * 0.15), rate: '7.5%', maturity: '2032-06-30', covenant: 'LTV ≤ 75%', headroom: '12.4%', status: 'Compliant' },
    { tranche: 'Equity', amount: Math.round(asset?.totalInvestmentValue * 0.30), rate: `${asset?.irr}% (target)`, maturity: 'N/A', covenant: '—', headroom: '—', status: '—' },
  ] : [
    { tranche: 'Equity (100%)', amount: asset?.totalInvestmentValue, rate: `${asset?.irr}% (target)`, maturity: 'N/A', covenant: '—', headroom: '—', status: '—' },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <SectionHeader title="Capital Structure" subtitle={asset?.debtStack ? 'Levered structure — senior debt, mezzanine, and equity' : 'Unlevered equity-only structure'} />
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                {['Tranche', 'Amount (£m)', 'Rate / Return', 'Maturity', 'Covenant', 'Headroom', 'Status']?.map((h) => (
                  <th key={h} className="px-4 py-3 text-left font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {debtLayers?.map((row) => (
                <tr key={row?.tranche} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3 font-caption text-sm font-semibold text-foreground">{row?.tranche}</td>
                  <td className="px-4 py-3 font-data text-sm text-foreground">{row?.amount}</td>
                  <td className="px-4 py-3 font-data text-sm text-foreground">{row?.rate}</td>
                  <td className="px-4 py-3 font-data text-sm text-muted-foreground">{row?.maturity}</td>
                  <td className="px-4 py-3 font-caption text-sm text-muted-foreground">{row?.covenant}</td>
                  <td className="px-4 py-3 font-data text-sm text-foreground">{row?.headroom}</td>
                  <td className="px-4 py-3">
                    {row?.status === 'Compliant' ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-caption font-medium">Compliant</span>
                    ) : (
                      <span className="font-caption text-xs text-muted-foreground">{row?.status}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {/* Waterfall */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <SectionHeader title="Distribution Waterfall" subtitle="Projected distributions to exit (sample)" />
          {[
            { year: 'Year 1–5', projected: `£${(asset?.totalInvestmentValue * 0.04)?.toFixed(1)}m p.a.`, actual: `£${(asset?.totalInvestmentValue * asset?.cashYieldActual / 100)?.toFixed(1)}m` },
            { year: 'Year 6–10', projected: `£${(asset?.totalInvestmentValue * 0.055)?.toFixed(1)}m p.a.`, actual: 'Forecast' },
            { year: 'Year 11–20', projected: `£${(asset?.totalInvestmentValue * 0.065)?.toFixed(1)}m p.a.`, actual: 'Forecast' },
            { year: 'Exit Proceeds', projected: `£${(asset?.totalInvestmentValue * asset?.multiple * 0.4)?.toFixed(1)}m`, actual: 'Projected' },
          ]?.map((row) => (
            <div key={row?.year} className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
              <span className="font-caption text-sm text-muted-foreground">{row?.year}</span>
              <div className="text-right">
                <p className="font-data text-sm font-medium text-foreground">{row?.projected}</p>
                <p className="font-caption text-xs text-muted-foreground">{row?.actual}</p>
              </div>
            </div>
          ))}
        </Card>

        <Card>
          <SectionHeader title="Holding Structure" />
          <div className="space-y-3">
            <InfoRow label="Shareholding" value={`${asset?.shareholdingPct}%`} mono />
            <InfoRow label="Governing Law" value={asset?.country === 'United Kingdom' ? 'English Law' : `${asset?.country} Law`} />
            <InfoRow label="Fund Vehicle" value={asset?.fund} />
            <InfoRow label="Debt Stack" value={asset?.debtStack ? 'Yes — levered' : 'No — equity only'} />
            <InfoRow label="Co-shareholders" value={asset?.counterparties?.filter(c => c?.type === 'Co-Shareholder')?.map(c => c?.name)?.join(', ') || 'None'} />
          </div>
        </Card>
      </div>
    </div>
  );
}

// ── TAB: IC MEMO ──────────────────────────────────────────────────────────────
function ICMemoTab({ asset }) {
  const ragStatus = (val, threshold) => val >= threshold ? '🟢' : val >= threshold * 0.85 ? '🟡' : '🔴';

  const thesisPoints = [
    { point: 'Long-term contracted revenue with creditworthy offtaker', status: asset?.irr >= 10 ? 'Green' : asset?.irr >= 8 ? 'Amber' : 'Red', commentary: asset?.outperformanceDrivers?.split('.')?.[0] || 'On track' },
    { point: 'Operational cost within budget', status: asset?.cashYieldActual >= asset?.cashYieldBudget ? 'Green' : asset?.cashYieldActual >= asset?.cashYieldBudget * 0.9 ? 'Amber' : 'Red', commentary: `Cash yield actual ${asset?.cashYieldActual}% vs budget ${asset?.cashYieldBudget}%` },
    { point: 'No material counterparty or regulatory risk', status: asset?.riskScore <= 2 ? 'Green' : asset?.riskScore <= 3 ? 'Amber' : 'Red', commentary: `Risk score ${asset?.riskScore}/5. ${asset?.disputesStatus === 'None' ? 'No disputes.' : `Disputes: ${asset?.disputesStatus}.`}` },
    { point: 'Stakeholder relationships maintained', status: asset?.stakeholderStatus === 'Strong' ? 'Green' : asset?.stakeholderStatus === 'Satisfactory' ? 'Amber' : 'Red', commentary: asset?.stakeholderStatus },
    { point: 'IRR tracking above underwrite', status: asset?.irr >= 10.5 ? 'Green' : asset?.irr >= 9 ? 'Amber' : 'Red', commentary: `Current IRR ${asset?.irr}% vs 10.5% underwrite` },
  ];

  const ragColour = { Green: 'bg-green-100 text-green-800', Amber: 'bg-amber-100 text-amber-800', Red: 'bg-red-100 text-red-800' };

  return (
    <div className="space-y-6">
      {/* Snapshot */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Net IRR', value: `${asset?.irr}%`, sub: 'vs 10.5% underwrite' },
          { label: 'Equity Multiple', value: `${asset?.multiple}x`, sub: 'vs 2.0x underwrite' },
          { label: 'Cash Yield (LTM)', value: `${asset?.cashYieldActual}%`, sub: `Budget: ${asset?.cashYieldBudget}%` },
          { label: 'Risk Score', value: `${asset?.riskScore}/5`, sub: asset?.watchlistStatus },
        ]?.map((m) => (
          <div key={m?.label} className="bg-card border border-border rounded-lg p-4 shadow-xs">
            <p className="font-data text-2xl font-bold text-foreground">{m?.value}</p>
            <p className="font-caption text-xs font-medium text-foreground mt-0.5">{m?.label}</p>
            <p className="font-caption text-xs text-muted-foreground mt-0.5">{m?.sub}</p>
          </div>
        ))}
      </div>
      {/* Acquisition thesis tracker */}
      <Card>
        <SectionHeader title="Acquisition Thesis Tracker" subtitle="RAG status against original IC investment thesis" />
        <div className="space-y-0">
          {thesisPoints?.map((tp, i) => (
            <div key={i} className="flex items-start gap-4 py-3 border-b border-border last:border-0">
              <span className={`text-xs px-2 py-0.5 rounded-full font-caption font-semibold flex-shrink-0 mt-0.5 ${ragColour?.[tp?.status]}`}>
                {tp?.status}
              </span>
              <div className="flex-1 min-w-0">
                <p className="font-caption text-sm font-medium text-foreground">{tp?.point}</p>
                <p className="font-caption text-xs text-muted-foreground mt-0.5">{tp?.commentary}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>
      {/* Key risks */}
      <Card>
        <SectionHeader title="Key Risks for IC" />
        <div className="space-y-3">
          {[
            { risk: 'Counterparty Risk', score: asset?.counterpartyRiskScore, detail: asset?.counterparties?.[0]?.notes || 'No material issues' },
            { risk: 'Dispute / Legal Risk', score: asset?.disputesStatus === 'None' ? 1 : asset?.disputesStatus === 'Minor' ? 3 : 5, detail: asset?.disputesStatus === 'None' ? 'No active disputes' : `Active dispute — ${asset?.situationUpdate?.split('.')?.[0]}` },
            { risk: 'Operational Risk', score: asset?.riskScore, detail: asset?.operationalFacts },
          ]?.map((r) => (
            <div key={r?.risk} className="flex items-start gap-4 p-3 bg-muted/30 rounded-md">
              <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${RISK_BG?.[r?.score]}`}>
                <span className={`font-data text-sm font-bold ${RISK_COLOURS?.[r?.score]}`}>{r?.score}</span>
              </div>
              <div>
                <p className="font-caption text-sm font-semibold text-foreground">{r?.risk}</p>
                <p className="font-caption text-xs text-muted-foreground mt-0.5">{r?.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>
      {/* Recommended action */}
      <Card>
        <SectionHeader title="Recommended Action" subtitle="Post-oversight decision" />
        <div className="flex items-start gap-3">
          <span className={`text-xs px-2.5 py-1 rounded-full font-caption font-semibold flex-shrink-0 ${WATCHLIST_COLOURS?.[asset?.watchlistStatus] || 'bg-gray-100 text-gray-700'}`}>
            {asset?.watchlistStatus}
          </span>
          <p className="font-body text-sm text-foreground leading-relaxed">{asset?.reviewSummary}</p>
        </div>
        {asset?.agreedActions && (
          <div className="mt-3 p-3 bg-primary/5 border border-primary/20 rounded-md">
            <p className="font-caption text-xs font-semibold text-primary mb-1">Agreed Actions</p>
            <p className="font-body text-sm text-foreground">{asset?.agreedActions}</p>
          </div>
        )}
      </Card>
    </div>
  );
}

// ── TAB: VALUE CREATION ───────────────────────────────────────────────────────
function ValueCreationTab({ asset }) {
  const allActions = [
    ...(asset?.valuationCreationActions?.map(a => ({ ...a, source: 'At Acquisition' })) || []),
    ...(asset?.valuationPostActions?.map(a => ({ ...a, source: 'Post Acquisition' })) || []),
  ];

  const totalImpact = allActions?.reduce((sum, a) => sum + (a?.valueImpact || 0), 0);
  const delivered = allActions?.filter(a => a?.status === 'Delivered');
  const inProgress = allActions?.filter(a => a?.status === 'In progress');

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Actions', value: allActions?.length, icon: 'List' },
          { label: 'Delivered', value: delivered?.length, icon: 'CheckCircle', colour: 'var(--color-success)' },
          { label: 'Total Value Impact', value: `£${totalImpact?.toFixed(1)}m`, icon: 'TrendingUp', colour: 'var(--color-primary)' },
        ]?.map((m) => (
          <div key={m?.label} className="bg-card border border-border rounded-lg p-4 shadow-xs">
            <div className="flex items-center gap-2 mb-2">
              <Icon name={m?.icon} size={15} color={m?.colour || 'var(--color-foreground)'} />
            </div>
            <p className="font-data text-2xl font-bold text-foreground">{m?.value}</p>
            <p className="font-caption text-xs text-muted-foreground mt-0.5">{m?.label}</p>
          </div>
        ))}
      </div>

      {[{ label: 'At Acquisition', items: asset?.valuationCreationActions }, { label: 'Post Acquisition', items: asset?.valuationPostActions }]?.map((group) => (
        group?.items?.length > 0 && (
          <Card key={group?.label}>
            <SectionHeader title={`Value Creation — ${group?.label}`} />
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    {['Action', 'Status', 'Owner', 'Target Date', 'Value Impact (£m)', 'Notes']?.map((h) => (
                      <th key={h} className="px-4 py-3 text-left font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {group?.items?.map((row) => (
                    <tr key={row?.id} className="border-b border-border last:border-0 hover:bg-muted/20 transition-colors">
                      <td className="px-4 py-3 font-caption text-sm font-medium text-foreground">{row?.action}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-caption font-semibold ${VALUE_STATUS_COLOURS?.[row?.status] || 'bg-gray-100 text-gray-700'}`}>
                          {row?.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-caption text-sm text-foreground">{row?.owner}</td>
                      <td className="px-4 py-3 font-data text-sm text-muted-foreground">{row?.targetDate}</td>
                      <td className="px-4 py-3 font-data text-sm font-semibold text-foreground">{row?.valueImpact}</td>
                      <td className="px-4 py-3 font-caption text-sm text-muted-foreground">{row?.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      ))}
    </div>
  );
}

// ── TAB: RISK & COUNTERPARTY ──────────────────────────────────────────────────
function RiskTab({ asset }) {
  const trendLabels = ['2 Reviews Ago', '1 Review Ago', 'Current'];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <SectionHeader title="Risk Profile" />
          <div className="space-y-4">
            {[
              { label: 'Overall Risk Score', value: asset?.riskScore, max: 5 },
              { label: 'Counterparty Risk Score', value: asset?.counterpartyRiskScore, max: 5 },
            ]?.map((r) => (
              <div key={r?.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-caption text-sm text-muted-foreground">{r?.label}</span>
                  <span className={`font-data text-sm font-bold ${RISK_COLOURS?.[r?.value]}`}>{r?.value}/{r?.max}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      r?.value <= 2 ? 'bg-green-500' : r?.value === 3 ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${(r?.value / r?.max) * 100}%` }}
                  />
                </div>
              </div>
            ))}
            <InfoRow label="Disputes Status" value={asset?.disputesStatus} />
            <InfoRow label="Stakeholder Status" value={asset?.stakeholderStatus} />
          </div>
        </Card>

        <Card>
          <SectionHeader title="Risk Trend" subtitle="Last 3 review cycles" />
          <div className="flex items-end gap-4 h-32 mt-4">
            {asset?.riskTrend?.map((score, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2">
                <span className={`font-data text-sm font-bold ${RISK_COLOURS?.[score]}`}>{score}</span>
                <div
                  className={`w-full rounded-t-md transition-all ${
                    score <= 2 ? 'bg-green-400' : score === 3 ? 'bg-amber-400' : 'bg-red-400'
                  }`}
                  style={{ height: `${(score / 5) * 80}px` }}
                />
                <span className="font-caption text-xs text-muted-foreground text-center">{trendLabels?.[i]}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card>
        <SectionHeader title="Situation Update" />
        <p className="font-body text-sm text-foreground leading-relaxed">{asset?.situationUpdate}</p>
      </Card>

      {/* Counterparties */}
      <Card>
        <SectionHeader title="Counterparty Directory" subtitle="External parties" />
        <div className="space-y-3">
          {asset?.counterparties?.map((cp, i) => (
            <div key={i} className="flex items-start gap-4 p-3 bg-muted/30 rounded-md">
              <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Building2" size={14} color="var(--color-primary)" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-caption text-sm font-semibold text-foreground">{cp?.name}</p>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-caption">{cp?.type}</span>
                </div>
                <p className="font-caption text-xs text-muted-foreground mt-0.5">{cp?.contact} · {cp?.phone}</p>
                {cp?.notes && <p className="font-caption text-xs text-foreground mt-1">{cp?.notes}</p>}
              </div>
              <span className="font-caption text-xs text-muted-foreground flex-shrink-0">Last: {cp?.lastContacted}</span>
            </div>
          ))}
        </div>
      </Card>

      {/* Internal contacts */}
      <Card>
        <SectionHeader title="Internal Contacts" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {asset?.internalContacts?.map((c, i) => (
            <div key={i} className="p-3 bg-muted/30 rounded-md">
              <p className="font-caption text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">{c?.role}</p>
              <p className="font-caption text-sm font-semibold text-foreground">{c?.name}</p>
              <p className="font-caption text-xs text-muted-foreground mt-0.5">{c?.email}</p>
              <p className="font-caption text-xs text-muted-foreground">{c?.phone}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ── TAB: GOVERNANCE ───────────────────────────────────────────────────────────
function GovernanceTab({ asset }) {
  const docs = asset?.governanceDocs;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <SectionHeader title="Document Status" />
          {[
            { label: 'Board Papers', value: docs?.boardPapers },
            { label: 'Financial Model', value: docs?.financialModel },
            { label: 'Finance Review', value: docs?.financeReview },
            { label: 'HSE Policy', value: docs?.hsePolicy },
            { label: 'ESG Policy', value: docs?.esgPolicy },
          ]?.map((d) => (
            <div key={d?.label} className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
              <span className="font-caption text-sm text-muted-foreground">{d?.label}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-caption font-medium ${
                d?.value === 'Linked' || d?.value === 'Complete' || d?.value === 'Current' ? 'bg-green-100 text-green-800' :
                d?.value === 'Pending' || d?.value === 'Pending update' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'
              }`}>{d?.value}</span>
            </div>
          ))}
        </Card>

        <Card>
          <SectionHeader title="Consistency Check" subtitle="Review information vs risk register" />
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              docs?.consistencyCheck ? 'bg-green-100' : 'bg-red-100'
            }`}>
              <Icon
                name={docs?.consistencyCheck ? 'CheckCircle' : 'XCircle'}
                size={20}
                color={docs?.consistencyCheck ? 'var(--color-success)' : 'var(--color-error)'}
              />
            </div>
            <div>
              <p className="font-caption text-sm font-semibold text-foreground">
                {docs?.consistencyCheck ? 'Consistent' : 'Discrepancy Identified'}
              </p>
              <p className="font-caption text-xs text-muted-foreground">Review vs risk register alignment</p>
            </div>
          </div>
          <p className="font-body text-sm text-foreground leading-relaxed">{docs?.consistencyCommentary}</p>
        </Card>
      </div>

      <Card>
        <SectionHeader title="Annual Valuation Notes" />
        <p className="font-body text-sm text-foreground leading-relaxed">{docs?.annualValuationNotes}</p>
        <div className="mt-4 flex items-center gap-2">
          <span className="font-caption text-xs text-muted-foreground">Valuation Status:</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-caption font-medium ${
            asset?.valuationStatus === 'Submitted' ? 'bg-green-100 text-green-800' :
            asset?.valuationStatus === 'Under review' ? 'bg-amber-100 text-amber-800' :
            asset?.valuationStatus === 'Not required' ? 'bg-slate-100 text-slate-700' : 'bg-gray-100 text-gray-700'
          }`}>{asset?.valuationStatus}</span>
        </div>
      </Card>
    </div>
  );
}

// ── TAB: AUDIT TRAIL ──────────────────────────────────────────────────────────
function AuditTab({ asset }) {
  const assetLog = AUDIT_LOG?.filter((e) => e?.asset === asset?.name);

  return (
    <div className="space-y-6">
      <Card>
        <SectionHeader title="Audit Trail" subtitle={`All recorded actions for ${asset?.name}`} />
        {assetLog?.length === 0 ? (
          <p className="font-caption text-sm text-muted-foreground py-4">No audit entries for this asset.</p>
        ) : (
          <div className="space-y-0">
            {assetLog?.map((entry) => (
              <div key={entry?.id} className="flex items-start gap-4 py-3.5 border-b border-border last:border-0">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon name="Clock" size={13} color="var(--color-primary)" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-caption text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{entry?.action}</span>
                    <span className="font-caption text-xs text-muted-foreground">by {entry?.user}</span>
                  </div>
                  <p className="font-body text-sm text-foreground mt-1">{entry?.detail}</p>
                </div>
                <span className="font-data text-xs text-muted-foreground flex-shrink-0">{entry?.timestamp}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function AssetDetailDrilldown() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('passport');
  const [userRole, setUserRole] = useState('investor');

  // Support both URL param and query string
  const searchParams = new URLSearchParams(location?.search);
  const qsId = searchParams?.get('id');
  const resolvedId = assetId || qsId;

  const asset = ASSETS?.find((a) => a?.id === resolvedId) || ASSETS?.[0];

  const renderTab = () => {
    switch (activeTab) {
      case 'passport': return <PassportTab asset={asset} navigate={navigate} />;
      case 'performance': return <PerformanceTab asset={asset} />;
      case 'underwrite': return <UnderwriteTab asset={asset} />;
      case 'debt': return <DebtTab asset={asset} />;
      case 'ic-memo': return <ICMemoTab asset={asset} />;
      case 'value-creation': return <ValueCreationTab asset={asset} />;
      case 'risk': return <RiskTab asset={asset} />;
      case 'governance': return <GovernanceTab asset={asset} />;
      case 'audit': return <AuditTab asset={asset} />;
      default: return null;
    }
  };

  // Determine back destination
  const fromAM = location?.state?.from === 'asset-management';
  const backPath = location?.state?.backPath || '/asset-management/console';
  const backLabel = location?.state?.backLabel || 'Asset Management';

  return (
    <AppLayout userRole={userRole} onRoleChange={setUserRole}>
      <div className="min-h-screen bg-background">
        {/* Sticky header */}
        <div
          className="sticky top-0 bg-background border-b border-border px-6 md:px-10 lg:px-12 py-4"
          style={{ zIndex: 'var(--z-nav)' }}
        >
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 mb-3 pl-10 lg:pl-0" aria-label="Breadcrumb">
            <button
              onClick={() => navigate('/asset-management/console')}
              className="font-caption text-muted-foreground text-xs hover:text-foreground transition-colors"
            >
              Asset Management
            </button>
            <Icon name="ChevronRight" size={12} color="var(--color-muted-foreground)" />
            <button
              onClick={() => navigate(backPath)}
              className="font-caption text-muted-foreground text-xs hover:text-foreground transition-colors"
            >
              {backLabel}
            </button>
            <Icon name="ChevronRight" size={12} color="var(--color-muted-foreground)" />
            <span className="font-caption text-foreground text-xs font-medium">{asset?.name}</span>
          </nav>

          {/* Header row */}
          <div className="flex flex-wrap items-start justify-between gap-4 pl-10 lg:pl-0">
            <div className="flex flex-wrap items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center">
                <Icon name="Building2" size={18} color="var(--color-primary)" strokeWidth={2} />
              </div>
              <div>
                <h1 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.375rem', lineHeight: 1.3 }}>
                  {asset?.name}
                </h1>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="font-caption text-xs text-muted-foreground">{asset?.sector} · {asset?.subSector} · {asset?.country}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-caption font-medium ${
                    asset?.stage === 'Operational' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                  }`}>{asset?.stage}</span>
                  <span className={`text-xs px-2.5 py-0.5 rounded-full font-caption font-semibold ${WATCHLIST_COLOURS?.[asset?.watchlistStatus] || 'bg-gray-100 text-gray-700'}`}>
                    {asset?.watchlistStatus}
                  </span>
                  <ReviewStatusBadge status={asset?.reviewStatus} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(backPath)}
                className="flex items-center gap-1.5 font-caption text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <Icon name="ArrowLeft" size={14} />
                Back
              </button>
              <Button
                variant="outline"
                size="sm"
                iconName="ExternalLink"
                iconPosition="left"
                iconSize={13}
                onClick={() => navigate(`/asset-detail-page?id=asset-1`)}
              >
                Portfolio View
              </Button>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="bg-card border-b border-border px-6 md:px-10 lg:px-12 overflow-x-auto">
          <div className="flex gap-0 min-w-max pl-10 lg:pl-0">
            {TABS?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-1.5 px-4 py-3.5 text-sm font-caption font-medium border-b-2 transition-all duration-150 whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                }`}
              >
                <Icon
                  name={tab?.icon}
                  size={14}
                  color={activeTab === tab?.id ? 'var(--color-primary)' : 'currentColor'}
                  strokeWidth={activeTab === tab?.id ? 2.5 : 2}
                />
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 md:px-10 lg:px-12 py-8">
          {renderTab()}
          <p className="text-center font-caption text-muted-foreground text-xs mt-8 pb-4">
            All figures are sample data for demonstration purposes only and do not represent actual investment performance.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
